const DEFAULT_IMAGE_OPTIONS = {
  // Good balance for mobile camera photos and upload speed.
  maxWidthOrHeight: 1600,
  maxBytes: 450 * 1024,
  initialQuality: 0.82,
  minQuality: 0.55,
  qualityStep: 0.07,
  skipSizeBelow: 250 * 1024,
};

function fileToBase64DataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(error);
    };
    image.src = objectUrl;
  });
}

function canvasToBlob(canvas, mimeType, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to create compressed image blob."));
        return;
      }
      resolve(blob);
    }, mimeType, quality);
  });
}

function getScaledDimensions(width, height, maxWidthOrHeight) {
  if (width <= maxWidthOrHeight && height <= maxWidthOrHeight) {
    return { width, height };
  }

  const scale = Math.min(maxWidthOrHeight / width, maxWidthOrHeight / height);
  return {
    width: Math.round(width * scale),
    height: Math.round(height * scale),
  };
}

function chooseOutputType(inputType) {
  if (inputType === "image/webp") return "image/webp";
  if (inputType === "image/png") return "image/png";
  return "image/jpeg";
}

export async function optimizeImageFileForUpload(file, options = {}) {
  if (!(file instanceof File) || !file.type.startsWith("image/")) {
    return file;
  }

  const config = { ...DEFAULT_IMAGE_OPTIONS, ...options };
  const {
    maxWidthOrHeight,
    maxBytes,
    initialQuality,
    minQuality,
    qualityStep,
    skipSizeBelow,
  } = config;

  // Keep already-small images untouched.
  if (file.size <= skipSizeBelow) {
    return file;
  }

  try {
    const image = await loadImage(file);
    const { width, height } = getScaledDimensions(
      image.naturalWidth || image.width,
      image.naturalHeight || image.height,
      maxWidthOrHeight,
    );

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (!ctx) return file;

    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(image, 0, 0, width, height);

    const outputType = chooseOutputType(file.type);
    let quality = initialQuality;
    let blob = await canvasToBlob(canvas, outputType, quality);

    if (outputType !== "image/png") {
      while (blob.size > maxBytes && quality > minQuality) {
        quality = Math.max(minQuality, quality - qualityStep);
        blob = await canvasToBlob(canvas, outputType, quality);
      }
    }

    // If optimization made it worse, keep original.
    if (blob.size >= file.size) {
      return file;
    }

    const nextName =
      outputType === "image/jpeg"
        ? file.name.replace(/\.[^.]+$/, "") + ".jpg"
        : file.name;

    return new File([blob], nextName, {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.warn("Image optimization skipped:", error);
    return file;
  }
}

export async function getOptimizedBase64ForUpload(file, options = {}) {
  const optimizedFile = await optimizeImageFileForUpload(file, options);
  const dataUrl = await fileToBase64DataUrl(optimizedFile);
  return dataUrl.replace(/^data:image\/[a-zA-Z0-9+]+;base64,/, "");
}
